import React, { useState, useEffect } from "react";
import { useScrollAnimation } from "../hooks/useScrollAnimation";

function CalculatorForm() {
  const [selectedShape, setSelectedShape] = useState("beams-heb");
  const [inputs, setInputs] = useState({
    diameter: "",
    width: "",
    height: "",
    thickness: "",
    length: "",
    pieces: "",
    profile: "HEB 100",
  });
  const [results, setResults] = useState({
    weightPerMeter: 0,
    totalWeight: 0,
    singlePieceWeight: 0,
  });

  const [titleRef, titleVisible] = useScrollAnimation();
  const [descRef, descVisible] = useScrollAnimation();
  const [contentRef, contentVisible] = useScrollAnimation();

  // Steel density in kg/mm³
  const STEEL_DENSITY = 0.00000785;

  // HEB (IPB) Beam profiles lookup table
  const HEB_PROFILES = {
    "HEB 100": { h: 100, b: 100, s: 6, t: 10, weightPerMeter: 20.4 },
    "HEB 120": { h: 120, b: 120, s: 6.5, t: 11, weightPerMeter: 26.7 },
    "HEB 140": { h: 140, b: 140, s: 7, t: 12, weightPerMeter: 33.7 },
    "HEB 160": { h: 160, b: 160, s: 8, t: 13, weightPerMeter: 42.6 },
    "HEB 180": { h: 180, b: 180, s: 8.5, t: 14, weightPerMeter: 51.2 },
    "HEB 200": { h: 200, b: 200, s: 9, t: 15, weightPerMeter: 61.3 },
    "HEB 220": { h: 220, b: 220, s: 9.5, t: 16, weightPerMeter: 71.5 },
    "HEB 240": { h: 240, b: 240, s: 10, t: 17, weightPerMeter: 83.2 },
    "HEB 260": { h: 260, b: 260, s: 10, t: 17.5, weightPerMeter: 93.0 },
    "HEB 280": { h: 280, b: 280, s: 10.5, t: 18, weightPerMeter: 103.0 },
    "HEB 300": { h: 300, b: 300, s: 11, t: 19, weightPerMeter: 117.0 },
    "HEB 320": { h: 320, b: 300, s: 11.5, t: 20.5, weightPerMeter: 127.0 },
    "HEB 340": { h: 340, b: 300, s: 12, t: 21.5, weightPerMeter: 134.0 },
    "HEB 360": { h: 360, b: 300, s: 12.5, t: 22.5, weightPerMeter: 142.0 },
    "HEB 400": { h: 400, b: 300, s: 13.5, t: 24, weightPerMeter: 155.0 },
    "HEB 450": { h: 450, b: 300, s: 14, t: 26, weightPerMeter: 171.0 },
    "HEB 500": { h: 500, b: 300, s: 14.5, t: 28, weightPerMeter: 187.0 },
    "HEB 550": { h: 550, b: 300, s: 15, t: 29, weightPerMeter: 199.0 },
    "HEB 600": { h: 600, b: 300, s: 15.5, t: 30, weightPerMeter: 212.0 },
    "HEB 650": { h: 650, b: 300, s: 16, t: 31, weightPerMeter: 225.0 },
    "HEB 700": { h: 700, b: 300, s: 17, t: 32, weightPerMeter: 241.0 },
    "HEB 800": { h: 800, b: 300, s: 17.5, t: 33, weightPerMeter: 262.0 },
    "HEB 900": { h: 900, b: 300, s: 18.5, t: 35, weightPerMeter: 291.0 },
    "HEB 1000": { h: 1000, b: 300, s: 19, t: 36, weightPerMeter: 314.0 },
  };

  // HEA (IPBL) Beam profiles lookup table
  const HEA_PROFILES = {
    "HEA 100": { h: 96, b: 100, s: 5, t: 8, weightPerMeter: 16.7 },
    "HEA 120": { h: 114, b: 120, s: 5, t: 8, weightPerMeter: 19.9 },
    "HEA 140": { h: 133, b: 140, s: 5.5, t: 8.5, weightPerMeter: 24.7 },
    "HEA 160": { h: 152, b: 160, s: 6, t: 9, weightPerMeter: 30.4 },
    "HEA 180": { h: 171, b: 180, s: 6, t: 9.5, weightPerMeter: 35.5 },
    "HEA 200": { h: 190, b: 200, s: 6.5, t: 10, weightPerMeter: 42.3 },
    "HEA 220": { h: 210, b: 220, s: 7, t: 11, weightPerMeter: 50.5 },
    "HEA 240": { h: 230, b: 240, s: 7.5, t: 12, weightPerMeter: 60.3 },
    "HEA 260": { h: 250, b: 260, s: 7.5, t: 12.5, weightPerMeter: 68.2 },
    "HEA 280": { h: 270, b: 280, s: 8, t: 13, weightPerMeter: 76.4 },
    "HEA 300": { h: 290, b: 300, s: 8.5, t: 14, weightPerMeter: 88.3 },
    "HEA 320": { h: 310, b: 300, s: 9, t: 15.5, weightPerMeter: 97.6 },
    "HEA 340": { h: 330, b: 300, s: 9.5, t: 16.5, weightPerMeter: 105.0 },
    "HEA 360": { h: 350, b: 300, s: 10, t: 17.5, weightPerMeter: 112.0 },
    "HEA 400": { h: 390, b: 300, s: 11, t: 19, weightPerMeter: 125.0 },
    "HEA 450": { h: 440, b: 300, s: 11.5, t: 21, weightPerMeter: 140.0 },
    "HEA 500": { h: 490, b: 300, s: 12, t: 23, weightPerMeter: 155.0 },
    "HEA 550": { h: 540, b: 300, s: 12.5, t: 24, weightPerMeter: 166.0 },
    "HEA 600": { h: 590, b: 300, s: 13, t: 25, weightPerMeter: 178.0 },
    "HEA 650": { h: 640, b: 300, s: 13.5, t: 26, weightPerMeter: 190.0 },
    "HEA 700": { h: 690, b: 300, s: 14.5, t: 27, weightPerMeter: 204.0 },
    "HEA 800": { h: 790, b: 300, s: 15, t: 28, weightPerMeter: 224.0 },
    "HEA 900": { h: 890, b: 300, s: 16, t: 30, weightPerMeter: 252.0 },
    "HEA 1000": { h: 990, b: 300, s: 16.5, t: 31, weightPerMeter: 272.0 },
  };

  // IPE Beam profiles lookup table
  const IPE_PROFILES = {
    "IPE 80": { h: 80, b: 46, s: 3.8, t: 5.2, weightPerMeter: 6.0 },
    "IPE 100": { h: 100, b: 55, s: 4.1, t: 5.7, weightPerMeter: 8.1 },
    "IPE 120": { h: 120, b: 64, s: 4.4, t: 6.3, weightPerMeter: 10.4 },
    "IPE 140": { h: 140, b: 73, s: 4.7, t: 6.9, weightPerMeter: 12.9 },
    "IPE 160": { h: 160, b: 82, s: 5, t: 7.4, weightPerMeter: 15.8 },
    "IPE 180": { h: 180, b: 91, s: 5.3, t: 8, weightPerMeter: 18.8 },
    "IPE 200": { h: 200, b: 100, s: 5.6, t: 8.5, weightPerMeter: 22.4 },
    "IPE 220": { h: 220, b: 110, s: 5.9, t: 9.2, weightPerMeter: 26.2 },
    "IPE 240": { h: 240, b: 120, s: 6.2, t: 9.8, weightPerMeter: 30.7 },
    "IPE 270": { h: 270, b: 135, s: 6.6, t: 10.2, weightPerMeter: 36.1 },
    "IPE 300": { h: 300, b: 150, s: 7.1, t: 10.7, weightPerMeter: 42.2 },
    "IPE 330": { h: 330, b: 160, s: 7.5, t: 11.5, weightPerMeter: 49.1 },
    "IPE 360": { h: 360, b: 170, s: 8, t: 12.7, weightPerMeter: 57.1 },
    "IPE 400": { h: 400, b: 180, s: 8.6, t: 13.5, weightPerMeter: 66.3 },
    "IPE 450": { h: 450, b: 190, s: 9.4, t: 14.6, weightPerMeter: 77.6 },
    "IPE 500": { h: 500, b: 200, s: 10.2, t: 16, weightPerMeter: 90.7 },
    "IPE 550": { h: 550, b: 210, s: 11.1, t: 17.2, weightPerMeter: 106.0 },
    "IPE 600": { h: 600, b: 220, s: 12, t: 19, weightPerMeter: 122.0 },
  };

  // IPN Beam profiles lookup table
  const IPN_PROFILES = {
    "IPN 80": { h: 80, b: 42, s: 3.9, t: 5.9, weightPerMeter: 5.94 },
    "IPN 100": { h: 100, b: 50, s: 4.5, t: 6.8, weightPerMeter: 8.34 },
    "IPN 120": { h: 120, b: 58, s: 5.1, t: 7.7, weightPerMeter: 11.1 },
    "IPN 140": { h: 140, b: 66, s: 5.7, t: 8.6, weightPerMeter: 14.3 },
    "IPN 160": { h: 160, b: 74, s: 6.3, t: 9.5, weightPerMeter: 17.9 },
    "IPN 180": { h: 180, b: 82, s: 6.9, t: 10.4, weightPerMeter: 21.9 },
    "IPN 200": { h: 200, b: 90, s: 7.5, t: 11.3, weightPerMeter: 26.2 },
    "IPN 220": { h: 220, b: 98, s: 8.1, t: 12.2, weightPerMeter: 31.1 },
    "IPN 240": { h: 240, b: 106, s: 8.7, t: 13.1, weightPerMeter: 36.2 },
    "IPN 260": { h: 260, b: 113, s: 9.4, t: 14.1, weightPerMeter: 41.9 },
    "IPN 280": { h: 280, b: 119, s: 10.1, t: 15.2, weightPerMeter: 47.9 },
    "IPN 300": { h: 300, b: 125, s: 10.8, t: 16.2, weightPerMeter: 54.2 },
    "IPN 320": { h: 320, b: 131, s: 11.5, t: 17.3, weightPerMeter: 61.0 },
    "IPN 340": { h: 340, b: 137, s: 12.2, t: 18.3, weightPerMeter: 68.0 },
    "IPN 360": { h: 360, b: 143, s: 13, t: 19.5, weightPerMeter: 76.1 },
    "IPN 380": { h: 380, b: 149, s: 13.7, t: 20.5, weightPerMeter: 84.0 },
    "IPN 400": { h: 400, b: 155, s: 14.4, t: 21.6, weightPerMeter: 92.4 },
    "IPN 450": { h: 450, b: 170, s: 16.2, t: 24.3, weightPerMeter: 115.0 },
    "IPN 500": { h: 500, b: 185, s: 18, t: 27, weightPerMeter: 141.0 },
    "IPN 550": { h: 550, b: 200, s: 19, t: 30, weightPerMeter: 166.0 },
  };

  // UPN Channel profiles lookup table
  const UPN_PROFILES = {
    "UPN 50 x 25": { h: 50, b: 25, s: 5, t: 6, weightPerMeter: 3.86 },
    "UPN 50": { h: 50, b: 38, s: 5, t: 7, weightPerMeter: 5.59 },
    "UPN 60": { h: 60, b: 30, s: 6, t: 6, weightPerMeter: 5.07 },
    "UPN 65": { h: 65, b: 42, s: 5.5, t: 7.5, weightPerMeter: 7.09 },
    "UPN 80": { h: 80, b: 45, s: 6, t: 8, weightPerMeter: 8.64 },
    "UPN 100": { h: 100, b: 50, s: 6, t: 8.5, weightPerMeter: 10.6 },
    "UPN 120": { h: 120, b: 55, s: 7, t: 9, weightPerMeter: 13.4 },
    "UPN 140": { h: 140, b: 60, s: 7, t: 10, weightPerMeter: 16.0 },
    "UPN 160": { h: 160, b: 65, s: 7.5, t: 10.5, weightPerMeter: 18.8 },
    "UPN 180": { h: 180, b: 70, s: 8, t: 11, weightPerMeter: 22.0 },
    "UPN 200": { h: 200, b: 75, s: 8.5, t: 11.5, weightPerMeter: 25.3 },
    "UPN 220": { h: 220, b: 80, s: 9, t: 12.5, weightPerMeter: 29.4 },
    "UPN 240": { h: 240, b: 85, s: 9.5, t: 13, weightPerMeter: 33.2 },
    "UPN 260": { h: 260, b: 90, s: 10, t: 14, weightPerMeter: 37.9 },
    "UPN 280": { h: 280, b: 95, s: 10, t: 15, weightPerMeter: 41.8 },
    "UPN 300": { h: 300, b: 100, s: 10, t: 16, weightPerMeter: 46.2 },
    "UPN 320": { h: 320, b: 100, s: 14, t: 17.5, weightPerMeter: 59.5 },
    "UPN 350": { h: 350, b: 100, s: 14, t: 16, weightPerMeter: 60.6 },
    "UPN 380": { h: 380, b: 102, s: 13.5, t: 16, weightPerMeter: 63.1 },
    "UPN 400": { h: 400, b: 110, s: 14, t: 18, weightPerMeter: 71.8 },
  };

  // GOST Channel profiles lookup table
  const GOST_PROFILES = {
    "№ 6.5": { h: 65, b: 36, s: 4.4, t: 7.2, weightPerMeter: 5.9 },
    "№ 8": { h: 80, b: 40, s: 4.2, t: 7.4, weightPerMeter: 7.03 },
    "№ 10": { h: 100, b: 46, s: 4.3, t: 7.6, weightPerMeter: 8.58 },
    "№ 12": { h: 120, b: 52, s: 4.3, t: 7.9, weightPerMeter: 10.22 },
    "№ 14": { h: 140, b: 58, s: 4.4, t: 8.2, weightPerMeter: 12.07 },
    "№ 16": { h: 160, b: 64, s: 4.4, t: 8.5, weightPerMeter: 13.83 },
    "№ 18": { h: 180, b: 70, s: 4.5, t: 9.2, weightPerMeter: 16.16 },
    "№ 20": { h: 200, b: 76, s: 4.6, t: 9.6, weightPerMeter: 18.41 },
    "№ 22": { h: 220, b: 80, s: 4.6, t: 10, weightPerMeter: 20.53 },
    "№ 24": { h: 240, b: 90, s: 4.8, t: 10.6, weightPerMeter: 23.7 },
    "№ 27": { h: 270, b: 95, s: 6, t: 10.5, weightPerMeter: 27.7 },
    "№ 30": { h: 300, b: 100, s: 6.3, t: 11.4, weightPerMeter: 32.09 },
  };

  // Equal Angles lookup table
  // Structure: { "sideA": { thickness: weightPerMeter } }
  const EQUAL_ANGLES = {
    20: {
      3: 0.88,
      4: 1.15,
    },
    25: {
      3: 1.12,
      4: 1.45,
    },
    30: {
      3: 1.36,
      4: 1.78,
      5: 2.18,
    },
    35: {
      3: 1.6,
      4: 2.09,
      5: 2.57,
    },
    40: {
      3: 1.85,
      4: 2.42,
      5: 2.97,
    },
    45: {
      3: 2.08,
      4: 2.74,
      5: 3.38,
    },
    50: {
      3: 2.32,
      4: 3.06,
      5: 3.77,
      6: 4.47,
      7: 5.15,
    },
    55: {
      4: 3.38,
      5: 4.18,
      6: 4.95,
    },
    60: {
      4: 3.7,
      5: 4.57,
      6: 5.42,
      7: 6.26,
      8: 7.09,
    },
    63: {
      4: 3.9,
      5: 4.81,
      6: 5.72,
      7: 6.61,
      8: 7.48,
    },
    65: {
      5: 4.97,
      6: 5.91,
      7: 6.83,
      8: 7.73,
    },
    70: {
      5: 5.38,
      6: 6.38,
      7: 7.38,
      8: 8.37,
    },
    75: {
      5: 5.8,
      6: 6.85,
      7: 7.94,
      8: 8.99,
      9: 10.07,
    },
    80: {
      6: 7.34,
      7: 8.51,
      8: 9.63,
      9: 10.8,
      10: 11.9,
    },
    90: {
      6: 8.33,
      7: 9.61,
      8: 10.9,
      9: 12.2,
      10: 13.4,
    },
    100: {
      7: 10.79,
      8: 12.2,
      9: 13.6,
      10: 15.0,
      12: 17.8,
    },
    110: {
      7: 11.89,
      8: 13.5,
      9: 15.0,
      10: 16.6,
      12: 19.7,
    },
    120: {
      8: 14.7,
      9: 16.5,
      10: 18.2,
      12: 21.6,
      13: 23.3,
    },
    125: {
      8: 15.46,
      9: 17.3,
      10: 19.1,
    },
    130: {
      8: 15.99,
      9: 17.9,
      10: 19.79,
      12: 23.6,
      13: 25.37,
      14: 27.2,
      15: 29.0,
    },
    140: {
      9: 19.41,
      10: 21.45,
      12: 25.5,
      13: 27.5,
    },
    150: {
      10: 23.0,
      12: 27.3,
      13: 29.5,
      14: 31.6,
      15: 33.8,
      16: 35.9,
      18: 40.1,
      20: 44.2,
    },
    160: {
      10: 24.67,
      12: 29.35,
      13: 31.6,
      14: 34.2,
      15: 36.2,
      16: 38.52,
      18: 43.0,
      20: 47.4,
    },
    180: {
      12: 33.12,
      13: 35.7,
      14: 38.3,
      15: 40.9,
      16: 43.5,
      18: 48.6,
      20: 53.7,
    },
    200: {
      14: 42.8,
      15: 45.6,
      16: 48.5,
      18: 54.3,
      20: 59.9,
    },
  };

  // Unequal Angles lookup table
  // Structure: { "AxB": { thickness: weightPerMeter } }
  const UNEQUAL_ANGLES = {
    "30x20": {
      3: 1.12,
      4: 1.46,
    },
    "40x20": {
      3: 1.35,
      4: 1.77,
    },
    "40x25": {
      4: 1.93,
    },
    "45x30": {
      3: 1.72,
      4: 2.25,
      5: 2.77,
    },
    "50x30": {
      4: 2.41,
      5: 2.96,
    },
    "60x30": {
      5: 3.36,
    },
    "60x40": {
      5: 3.76,
      6: 4.46,
      7: 5.14,
    },
    "60x50": {
      5: 4.15,
      6: 4.93,
      8: 6.43,
      10: 7.88,
    },
    "65x50": {
      5: 4.35,
    },
    "70x50": {
      6: 5.41,
    },
    "75x50": {
      5: 4.74,
      6: 5.65,
      8: 7.39,
      9: 8.23,
      10: 9.05,
    },
    "75x55": {
      5: 4.95,
      7: 6.8,
      9: 8.59,
    },
    "75x60": {
      5: 5.14,
      6: 6.11,
      8: 8.01,
      10: 9.85,
    },
    "80x40": {
      6: 5.41,
      8: 7.07,
      10: 8.67,
    },
    "80x60": {
      7: 7.36,
    },
    "90x60": {
      6: 6.82,
      8: 8.96,
      10: 11.04,
    },
    "90x75": {
      6: 7.55,
      8: 9.93,
      10: 12.23,
    },
    "100x50": {
      6: 6.84,
      8: 8.97,
      10: 11.1,
      12: 13.06,
    },
    "100x65": {
      7: 8.77,
      8: 9.94,
      9: 11.1,
      10: 12.3,
    },
    "100x75": {
      6: 8.04,
      7: 9.32,
      8: 10.6,
      9: 11.8,
      10: 13.0,
      12: 15.4,
    },
    "100x90": {
      6: 8.77,
      8: 11.53,
      10: 14.23,
      12: 16.87,
    },
    "120x80": {
      8: 12.2,
      10: 15.0,
      12: 17.8,
    },
    "125x75": {
      8: 12.2,
      10: 15.0,
      12: 17.8,
    },
    "125x80": {
      8: 12.58,
      10: 15.47,
      12: 18.34,
      15: 22.47,
    },
    "125x90": {
      8: 13.12,
      10: 16.21,
      12: 19.24,
      15: 23.65,
    },
    "130x65": {
      8: 11.9,
      10: 14.6,
      12: 17.3,
    },
    "130x75": {
      8: 12.47,
      10: 15.41,
      12: 18.28,
      15: 22.47,
    },
    "130x90": {
      10: 16.6,
      12: 19.7,
    },
    "150x75": {
      9: 15.4,
      10: 17.0,
      12: 20.2,
      15: 24.8,
    },
    "150x90": {
      10: 18.2,
      12: 21.6,
      15: 26.6,
    },
    "150x100": {
      10: 19.0,
      12: 22.5,
    },
    "180x90": {
      10: 20.6,
      12: 24.5,
    },
    "200x100": {
      10: 23.0,
      12: 27.3,
      15: 33.75,
    },
    "200x150": {
      12: 32.0,
      15: 39.6,
    },
  };

  // Recalculate when inputs change
  useEffect(() => {
    const d = parseFloat(inputs.diameter) || 0;
    const w = parseFloat(inputs.width) || 0;
    const h = parseFloat(inputs.height) || 0;
    const t = parseFloat(inputs.thickness) || 0;
    const l = parseFloat(inputs.length) || 0;
    const pieces = parseFloat(inputs.pieces) || 0;

    let weightPerMeter = 0;
    let singlePieceWeight = 0;
    let totalWeight = 0;

    switch (selectedShape) {
      case "steel-sheets":
        // Steel sheets and plates: Volume = t × W × L (in mm³), Weight = Volume × density
        if (t > 0 && w > 0 && l > 0) {
          singlePieceWeight = t * w * l * STEEL_DENSITY;
          totalWeight = singlePieceWeight * (pieces || 1);
        }
        break;

      case "seamless-circular":
      case "hollow-circular":
        // Circular pipes: π × (D - t) × t × 1000mm × density
        if (d > 0 && t > 0 && d > t) {
          weightPerMeter = Math.PI * (d - t) * t * 1000 * STEEL_DENSITY;
          totalWeight = weightPerMeter * l;
        }
        break;

      case "hollow-square":
        // Square hollow sections: 4 × (A - t) × t × 1000mm × density
        if (w > 0 && t > 0 && w > t) {
          weightPerMeter = 4 * (w - t) * t * 1000 * STEEL_DENSITY;
          totalWeight = weightPerMeter * l;
        }
        break;

      case "hollow-rectangular":
        // Rectangular hollow sections: 2 × (A + B - 2t) × t × 1000mm × density
        if (w > 0 && h > 0 && t > 0 && w > 2 * t && h > 2 * t) {
          weightPerMeter = 2 * (w + h - 2 * t) * t * 1000 * STEEL_DENSITY;
          totalWeight = weightPerMeter * l;
        }
        break;

      case "round-bars":
        // Round steel bars: (π × D² / 4) × 1000mm × density
        if (d > 0) {
          weightPerMeter = ((Math.PI * d * d) / 4) * 1000 * STEEL_DENSITY;
          totalWeight = weightPerMeter * l;
        }
        break;

      case "square-bars":
        // Square steel bars: A² × 1000mm × density
        if (w > 0) {
          weightPerMeter = w * w * 1000 * STEEL_DENSITY;
          totalWeight = weightPerMeter * l;
        }
        break;

      case "flat-bars":
        // Flat bars: W × t × 1000mm × density
        if (w > 0 && t > 0) {
          weightPerMeter = w * t * 1000 * STEEL_DENSITY;
          totalWeight = weightPerMeter * l;
        }
        break;

      case "equal-angles":
        // Equal angles: Lookup table based on Side A and thickness
        const sideA = inputs.width; // This will store the side dimension like "20", "25", etc.
        const thicknessEqual = inputs.thickness;
        if (
          sideA &&
          thicknessEqual &&
          EQUAL_ANGLES[sideA] &&
          EQUAL_ANGLES[sideA][thicknessEqual]
        ) {
          weightPerMeter = EQUAL_ANGLES[sideA][thicknessEqual];
          totalWeight = weightPerMeter * l;
        }
        break;

      case "unequal-angles":
        // Unequal angles: Lookup table based on Side A x B and thickness
        const sideAxB = inputs.width; // This will store the "AxB" string like "30x20"
        const thicknessUnequal = inputs.thickness;
        if (
          sideAxB &&
          thicknessUnequal &&
          UNEQUAL_ANGLES[sideAxB] &&
          UNEQUAL_ANGLES[sideAxB][thicknessUnequal]
        ) {
          weightPerMeter = UNEQUAL_ANGLES[sideAxB][thicknessUnequal];
          totalWeight = weightPerMeter * l;
        }
        break;

      case "beams-heb":
        // HEB beams: Lookup table for weight per meter
        if (inputs.profile && HEB_PROFILES[inputs.profile]) {
          weightPerMeter = HEB_PROFILES[inputs.profile].weightPerMeter;
          totalWeight = weightPerMeter * l;
        }
        break;

      case "beams-hea":
        // HEA beams: Lookup table for weight per meter
        if (inputs.profile && HEA_PROFILES[inputs.profile]) {
          weightPerMeter = HEA_PROFILES[inputs.profile].weightPerMeter;
          totalWeight = weightPerMeter * l;
        }
        break;

      case "beams-ipe":
        // IPE beams: Lookup table for weight per meter
        if (inputs.profile && IPE_PROFILES[inputs.profile]) {
          weightPerMeter = IPE_PROFILES[inputs.profile].weightPerMeter;
          totalWeight = weightPerMeter * l;
        }
        break;

      case "beams-ipn":
        // IPN beams: Lookup table for weight per meter
        if (inputs.profile && IPN_PROFILES[inputs.profile]) {
          weightPerMeter = IPN_PROFILES[inputs.profile].weightPerMeter;
          totalWeight = weightPerMeter * l;
        }
        break;

      case "channels-upn":
        // UPN channels: Lookup table for weight per meter
        if (inputs.profile && UPN_PROFILES[inputs.profile]) {
          weightPerMeter = UPN_PROFILES[inputs.profile].weightPerMeter;
          totalWeight = weightPerMeter * l;
        }
        break;

      case "channels-gost":
        // GOST channels: Lookup table for weight per meter
        if (inputs.profile && GOST_PROFILES[inputs.profile]) {
          weightPerMeter = GOST_PROFILES[inputs.profile].weightPerMeter;
          totalWeight = weightPerMeter * l;
        }
        break;

      default:
        break;
    }

    setResults({
      weightPerMeter: weightPerMeter.toFixed(1),
      totalWeight: totalWeight.toFixed(1),
      singlePieceWeight: singlePieceWeight.toFixed(1),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputs, selectedShape, STEEL_DENSITY]);

  const handleInputChange = (field, value) => {
    setInputs((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleShapeChange = (shape) => {
    setSelectedShape(shape);
    // Reset inputs when changing shape
    let defaultProfile = "";
    let defaultWidth = "";
    let defaultThickness = "";

    if (shape === "beams-heb") {
      defaultProfile = "HEB 100";
    } else if (shape === "beams-hea") {
      defaultProfile = "HEA 100";
    } else if (shape === "beams-ipe") {
      defaultProfile = "IPE 80";
    } else if (shape === "beams-ipn") {
      defaultProfile = "IPN 80";
    } else if (shape === "channels-upn") {
      defaultProfile = "UPN 50 x 25";
    } else if (shape === "channels-gost") {
      defaultProfile = "№ 6.5";
    } else if (shape === "equal-angles") {
      defaultWidth = "20";
      defaultThickness = "3";
    } else if (shape === "unequal-angles") {
      defaultWidth = "30x20";
      defaultThickness = "3";
    }

    setInputs({
      diameter: "",
      width: defaultWidth,
      height: "",
      thickness: defaultThickness,
      length: "",
      pieces: "",
      profile: defaultProfile,
    });
  };

  const getInputFields = () => {
    switch (selectedShape) {
      case "steel-sheets":
        return (
          <>
            <div className="flex calculatorform-input">
              <h4 className="fs-300">Thickness, t</h4>
              <input
                type="number"
                placeholder="0 mm"
                value={inputs.thickness}
                onChange={(e) => handleInputChange("thickness", e.target.value)}
              />
            </div>
            <div className="flex calculatorform-input">
              <h4 className="fs-300">Width, W</h4>
              <input
                type="number"
                placeholder="0 mm"
                value={inputs.width}
                onChange={(e) => handleInputChange("width", e.target.value)}
              />
            </div>
            <div className="flex calculatorform-input">
              <h4 className="fs-300">Length, L</h4>
              <input
                type="number"
                placeholder="0 mm"
                value={inputs.length}
                onChange={(e) => handleInputChange("length", e.target.value)}
              />
            </div>
            <div className="flex calculatorform-input">
              <h4 className="fs-300">Number of pieces</h4>
              <input
                type="number"
                placeholder="1"
                value={inputs.pieces}
                onChange={(e) => handleInputChange("pieces", e.target.value)}
              />
            </div>
          </>
        );

      case "seamless-circular":
      case "hollow-circular":
        return (
          <>
            <div className="flex calculatorform-input">
              <h4 className="fs-300">Diameter, D</h4>
              <input
                type="number"
                placeholder="0 mm"
                value={inputs.diameter}
                onChange={(e) => handleInputChange("diameter", e.target.value)}
              />
            </div>
            <div className="flex calculatorform-input">
              <h4 className="fs-300">Thickness, t</h4>
              <input
                type="number"
                placeholder="0 mm"
                value={inputs.thickness}
                onChange={(e) => handleInputChange("thickness", e.target.value)}
              />
            </div>
            <div className="flex calculatorform-input">
              <h4 className="fs-300">Length, L</h4>
              <input
                type="number"
                placeholder="0 m"
                value={inputs.length}
                onChange={(e) => handleInputChange("length", e.target.value)}
              />
            </div>
          </>
        );

      case "hollow-square":
        return (
          <>
            <div className="flex calculatorform-input">
              <h4 className="fs-300">Side, A</h4>
              <input
                type="number"
                placeholder="0 mm"
                value={inputs.width}
                onChange={(e) => handleInputChange("width", e.target.value)}
              />
            </div>
            <div className="flex calculatorform-input">
              <h4 className="fs-300">Thickness, t</h4>
              <input
                type="number"
                placeholder="0 mm"
                value={inputs.thickness}
                onChange={(e) => handleInputChange("thickness", e.target.value)}
              />
            </div>
            <div className="flex calculatorform-input">
              <h4 className="fs-300">Length, L</h4>
              <input
                type="number"
                placeholder="0 m"
                value={inputs.length}
                onChange={(e) => handleInputChange("length", e.target.value)}
              />
            </div>
          </>
        );

      case "hollow-rectangular":
        return (
          <>
            <div className="flex calculatorform-input">
              <h4 className="fs-300">Side, A</h4>
              <input
                type="number"
                placeholder="0 mm"
                value={inputs.width}
                onChange={(e) => handleInputChange("width", e.target.value)}
              />
            </div>
            <div className="flex calculatorform-input">
              <h4 className="fs-300">Side, B</h4>
              <input
                type="number"
                placeholder="0 mm"
                value={inputs.height}
                onChange={(e) => handleInputChange("height", e.target.value)}
              />
            </div>
            <div className="flex calculatorform-input">
              <h4 className="fs-300">Thickness, t</h4>
              <input
                type="number"
                placeholder="0 mm"
                value={inputs.thickness}
                onChange={(e) => handleInputChange("thickness", e.target.value)}
              />
            </div>
            <div className="flex calculatorform-input">
              <h4 className="fs-300">Length, L</h4>
              <input
                type="number"
                placeholder="0 m"
                value={inputs.length}
                onChange={(e) => handleInputChange("length", e.target.value)}
              />
            </div>
          </>
        );

      case "round-bars":
        return (
          <>
            <div className="flex calculatorform-input">
              <h4 className="fs-300">Diameter, D</h4>
              <input
                type="number"
                placeholder="0 mm"
                value={inputs.diameter}
                onChange={(e) => handleInputChange("diameter", e.target.value)}
              />
            </div>
            <div className="flex calculatorform-input">
              <h4 className="fs-300">Length, L</h4>
              <input
                type="number"
                placeholder="0 m"
                value={inputs.length}
                onChange={(e) => handleInputChange("length", e.target.value)}
              />
            </div>
          </>
        );

      case "square-bars":
        return (
          <>
            <div className="flex calculatorform-input">
              <h4 className="fs-300">Side, A</h4>
              <input
                type="number"
                placeholder="0 mm"
                value={inputs.width}
                onChange={(e) => handleInputChange("width", e.target.value)}
              />
            </div>
            <div className="flex calculatorform-input">
              <h4 className="fs-300">Length, L</h4>
              <input
                type="number"
                placeholder="0 m"
                value={inputs.length}
                onChange={(e) => handleInputChange("length", e.target.value)}
              />
            </div>
          </>
        );

      case "flat-bars":
        return (
          <>
            <div className="flex calculatorform-input">
              <h4 className="fs-300">Side, W</h4>
              <input
                type="number"
                placeholder="0 mm"
                value={inputs.width}
                onChange={(e) => handleInputChange("width", e.target.value)}
              />
            </div>
            <div className="flex calculatorform-input">
              <h4 className="fs-300">Thickness, t</h4>
              <input
                type="number"
                placeholder="0 mm"
                value={inputs.thickness}
                onChange={(e) => handleInputChange("thickness", e.target.value)}
              />
            </div>
            <div className="flex calculatorform-input">
              <h4 className="fs-300">Length, L</h4>
              <input
                type="number"
                placeholder="0 m"
                value={inputs.length}
                onChange={(e) => handleInputChange("length", e.target.value)}
              />
            </div>
          </>
        );

      case "equal-angles":
        const selectedSideA = inputs.width; // Store side dimension like "20", "25", etc.
        const availableThicknessesEqual =
          selectedSideA && EQUAL_ANGLES[selectedSideA]
            ? Object.keys(EQUAL_ANGLES[selectedSideA])
            : [];

        return (
          <>
            <div className="flex calculatorform-input">
              <h4 className="fs-300">Side, A</h4>
              <select
                value={inputs.width || ""}
                onChange={(e) => {
                  setInputs((prev) => ({
                    ...prev,
                    width: e.target.value,
                    thickness: "", // Reset thickness when side changes
                  }));
                }}
                style={{
                  border: "1px solid #3e784d",
                  padding: "4px",
                  width: "88px",
                  backgroundColor: "transparent",
                }}
              >
                <option value="">Select</option>
                {Object.keys(EQUAL_ANGLES).map((side) => (
                  <option key={side} value={side}>
                    {side} mm
                  </option>
                ))}
              </select>
            </div>
            <div className="flex calculatorform-input">
              <h4 className="fs-300">Thickness, t</h4>
              <select
                value={inputs.thickness || ""}
                onChange={(e) => handleInputChange("thickness", e.target.value)}
                style={{
                  border: "1px solid #3e784d",
                  padding: "4px",
                  width: "88px",
                  backgroundColor: "transparent",
                }}
                disabled={!selectedSideA}
              >
                <option value="">Select</option>
                {availableThicknessesEqual.map((thickness) => (
                  <option key={thickness} value={thickness}>
                    {thickness} mm
                  </option>
                ))}
              </select>
            </div>
            <div className="flex calculatorform-input">
              <h4 className="fs-300">Length, L</h4>
              <input
                type="number"
                placeholder="0 m"
                value={inputs.length}
                onChange={(e) => handleInputChange("length", e.target.value)}
              />
            </div>
          </>
        );

      case "unequal-angles":
        const selectedSideAxB = inputs.width; // Store "AxB" as string like "30x20"
        const availableThicknesses =
          selectedSideAxB && UNEQUAL_ANGLES[selectedSideAxB]
            ? Object.keys(UNEQUAL_ANGLES[selectedSideAxB])
            : [];

        return (
          <>
            <div className="flex calculatorform-input">
              <h4 className="fs-300">Side, A x B</h4>
              <select
                value={inputs.width || ""}
                onChange={(e) => {
                  setInputs((prev) => ({
                    ...prev,
                    width: e.target.value,
                    thickness: "", // Reset thickness when side changes
                  }));
                }}
                style={{
                  border: "1px solid #3e784d",
                  padding: "4px",
                  width: "88px",
                  backgroundColor: "transparent",
                }}
              >
                <option value="">Select</option>
                {Object.keys(UNEQUAL_ANGLES).map((side) => (
                  <option key={side} value={side}>
                    {side} mm
                  </option>
                ))}
              </select>
            </div>
            <div className="flex calculatorform-input">
              <h4 className="fs-300">Thickness, t</h4>
              <select
                value={inputs.thickness || ""}
                onChange={(e) => handleInputChange("thickness", e.target.value)}
                style={{
                  border: "1px solid #3e784d",
                  padding: "4px",
                  width: "88px",
                  backgroundColor: "transparent",
                }}
                disabled={!selectedSideAxB}
              >
                <option value="">Select</option>
                {availableThicknesses.map((thickness) => (
                  <option key={thickness} value={thickness}>
                    {thickness} mm
                  </option>
                ))}
              </select>
            </div>
            <div className="flex calculatorform-input">
              <h4 className="fs-300">Length, L</h4>
              <input
                type="number"
                placeholder="0 m"
                value={inputs.length}
                onChange={(e) => handleInputChange("length", e.target.value)}
              />
            </div>
          </>
        );

      case "beams-heb":
        const selectedHEB = inputs.profile && HEB_PROFILES[inputs.profile];
        return (
          <>
            <div className="flex calculatorform-input">
              <h4 className="fs-300">Profile</h4>
              <select
                value={inputs.profile}
                onChange={(e) => handleInputChange("profile", e.target.value)}
                style={{
                  border: "1px solid #3e784d",
                  padding: "4px",
                  width: "120px",
                  backgroundColor: "transparent",
                }}
              >
                <option value="">Select</option>
                {Object.keys(HEB_PROFILES).map((profile) => (
                  <option key={profile} value={profile}>
                    {profile}
                  </option>
                ))}
              </select>
            </div>
            {selectedHEB && (
              <>
                <div className="flex calculatorform-input">
                  <h4 className="fs-300">Height, h</h4>
                  <input
                    type="text"
                    value={`${selectedHEB.h} mm`}
                    readOnly
                    style={{
                      backgroundColor: "#f5f5f5",
                      cursor: "not-allowed",
                    }}
                  />
                </div>
                <div className="flex calculatorform-input">
                  <h4 className="fs-300">Width, b</h4>
                  <input
                    type="text"
                    value={`${selectedHEB.b} mm`}
                    readOnly
                    style={{
                      backgroundColor: "#f5f5f5",
                      cursor: "not-allowed",
                    }}
                  />
                </div>
                <div className="flex calculatorform-input">
                  <h4 className="fs-300">Thickness, s</h4>
                  <input
                    type="text"
                    value={`${selectedHEB.s} mm`}
                    readOnly
                    style={{
                      backgroundColor: "#f5f5f5",
                      cursor: "not-allowed",
                    }}
                  />
                </div>
                <div className="flex calculatorform-input">
                  <h4 className="fs-300">Thickness, t</h4>
                  <input
                    type="text"
                    value={`${selectedHEB.t} mm`}
                    readOnly
                    style={{
                      backgroundColor: "#f5f5f5",
                      cursor: "not-allowed",
                    }}
                  />
                </div>
              </>
            )}
            <div className="flex calculatorform-input">
              <h4 className="fs-300">Length, L</h4>
              <input
                type="number"
                placeholder="0 m"
                value={inputs.length}
                onChange={(e) => handleInputChange("length", e.target.value)}
              />
            </div>
          </>
        );

      case "beams-hea":
        const selectedHEA = inputs.profile && HEA_PROFILES[inputs.profile];
        return (
          <>
            <div className="flex calculatorform-input">
              <h4 className="fs-300">Profile</h4>
              <select
                value={inputs.profile}
                onChange={(e) => handleInputChange("profile", e.target.value)}
                style={{
                  border: "1px solid #3e784d",
                  padding: "4px",
                  width: "120px",
                  backgroundColor: "transparent",
                }}
              >
                <option value="">Select</option>
                {Object.keys(HEA_PROFILES).map((profile) => (
                  <option key={profile} value={profile}>
                    {profile}
                  </option>
                ))}
              </select>
            </div>
            {selectedHEA && (
              <>
                <div className="flex calculatorform-input">
                  <h4 className="fs-300">Height, h</h4>
                  <input
                    type="text"
                    value={`${selectedHEA.h} mm`}
                    readOnly
                    style={{
                      backgroundColor: "#f5f5f5",
                      cursor: "not-allowed",
                    }}
                  />
                </div>
                <div className="flex calculatorform-input">
                  <h4 className="fs-300">Width, b</h4>
                  <input
                    type="text"
                    value={`${selectedHEA.b} mm`}
                    readOnly
                    style={{
                      backgroundColor: "#f5f5f5",
                      cursor: "not-allowed",
                    }}
                  />
                </div>
                <div className="flex calculatorform-input">
                  <h4 className="fs-300">Thickness, s</h4>
                  <input
                    type="text"
                    value={`${selectedHEA.s} mm`}
                    readOnly
                    style={{
                      backgroundColor: "#f5f5f5",
                      cursor: "not-allowed",
                    }}
                  />
                </div>
                <div className="flex calculatorform-input">
                  <h4 className="fs-300">Thickness, t</h4>
                  <input
                    type="text"
                    value={`${selectedHEA.t} mm`}
                    readOnly
                    style={{
                      backgroundColor: "#f5f5f5",
                      cursor: "not-allowed",
                    }}
                  />
                </div>
              </>
            )}
            <div className="flex calculatorform-input">
              <h4 className="fs-300">Length, L</h4>
              <input
                type="number"
                placeholder="0 m"
                value={inputs.length}
                onChange={(e) => handleInputChange("length", e.target.value)}
              />
            </div>
          </>
        );

      case "beams-ipe":
        const selectedIPE = inputs.profile && IPE_PROFILES[inputs.profile];
        return (
          <>
            <div className="flex calculatorform-input">
              <h4 className="fs-300">Profile</h4>
              <select
                value={inputs.profile}
                onChange={(e) => handleInputChange("profile", e.target.value)}
                style={{
                  border: "1px solid #3e784d",
                  padding: "4px",
                  width: "120px",
                  backgroundColor: "transparent",
                }}
              >
                <option value="">Select</option>
                {Object.keys(IPE_PROFILES).map((profile) => (
                  <option key={profile} value={profile}>
                    {profile}
                  </option>
                ))}
              </select>
            </div>
            {selectedIPE && (
              <>
                <div className="flex calculatorform-input">
                  <h4 className="fs-300">Height, h</h4>
                  <input
                    type="text"
                    value={`${selectedIPE.h} mm`}
                    readOnly
                    style={{
                      backgroundColor: "#f5f5f5",
                      cursor: "not-allowed",
                    }}
                  />
                </div>
                <div className="flex calculatorform-input">
                  <h4 className="fs-300">Width, b</h4>
                  <input
                    type="text"
                    value={`${selectedIPE.b} mm`}
                    readOnly
                    style={{
                      backgroundColor: "#f5f5f5",
                      cursor: "not-allowed",
                    }}
                  />
                </div>
                <div className="flex calculatorform-input">
                  <h4 className="fs-300">Thickness, s</h4>
                  <input
                    type="text"
                    value={`${selectedIPE.s} mm`}
                    readOnly
                    style={{
                      backgroundColor: "#f5f5f5",
                      cursor: "not-allowed",
                    }}
                  />
                </div>
                <div className="flex calculatorform-input">
                  <h4 className="fs-300">Thickness, t</h4>
                  <input
                    type="text"
                    value={`${selectedIPE.t} mm`}
                    readOnly
                    style={{
                      backgroundColor: "#f5f5f5",
                      cursor: "not-allowed",
                    }}
                  />
                </div>
              </>
            )}
            <div className="flex calculatorform-input">
              <h4 className="fs-300">Length, L</h4>
              <input
                type="number"
                placeholder="0 m"
                value={inputs.length}
                onChange={(e) => handleInputChange("length", e.target.value)}
              />
            </div>
          </>
        );

      case "beams-ipn":
        const selectedIPN = inputs.profile && IPN_PROFILES[inputs.profile];
        return (
          <>
            <div className="flex calculatorform-input">
              <h4 className="fs-300">Profile</h4>
              <select
                value={inputs.profile}
                onChange={(e) => handleInputChange("profile", e.target.value)}
                style={{
                  border: "1px solid #3e784d",
                  padding: "4px",
                  width: "120px",
                  backgroundColor: "transparent",
                }}
              >
                <option value="">Select</option>
                {Object.keys(IPN_PROFILES).map((profile) => (
                  <option key={profile} value={profile}>
                    {profile}
                  </option>
                ))}
              </select>
            </div>
            {selectedIPN && (
              <>
                <div className="flex calculatorform-input">
                  <h4 className="fs-300">Height, h</h4>
                  <input
                    type="text"
                    value={`${selectedIPN.h} mm`}
                    readOnly
                    style={{
                      backgroundColor: "#f5f5f5",
                      cursor: "not-allowed",
                    }}
                  />
                </div>
                <div className="flex calculatorform-input">
                  <h4 className="fs-300">Width, b</h4>
                  <input
                    type="text"
                    value={`${selectedIPN.b} mm`}
                    readOnly
                    style={{
                      backgroundColor: "#f5f5f5",
                      cursor: "not-allowed",
                    }}
                  />
                </div>
                <div className="flex calculatorform-input">
                  <h4 className="fs-300">Thickness, s</h4>
                  <input
                    type="text"
                    value={`${selectedIPN.s} mm`}
                    readOnly
                    style={{
                      backgroundColor: "#f5f5f5",
                      cursor: "not-allowed",
                    }}
                  />
                </div>
                <div className="flex calculatorform-input">
                  <h4 className="fs-300">Thickness, t</h4>
                  <input
                    type="text"
                    value={`${selectedIPN.t} mm`}
                    readOnly
                    style={{
                      backgroundColor: "#f5f5f5",
                      cursor: "not-allowed",
                    }}
                  />
                </div>
              </>
            )}
            <div className="flex calculatorform-input">
              <h4 className="fs-300">Length, L</h4>
              <input
                type="number"
                placeholder="0 m"
                value={inputs.length}
                onChange={(e) => handleInputChange("length", e.target.value)}
              />
            </div>
          </>
        );

      case "channels-upn":
        const selectedUPN = inputs.profile && UPN_PROFILES[inputs.profile];
        return (
          <>
            <div className="flex calculatorform-input">
              <h4 className="fs-300">Profile</h4>
              <select
                value={inputs.profile}
                onChange={(e) => handleInputChange("profile", e.target.value)}
                style={{
                  border: "1px solid #3e784d",
                  padding: "4px",
                  width: "120px",
                  backgroundColor: "transparent",
                }}
              >
                <option value="">Select</option>
                {Object.keys(UPN_PROFILES).map((profile) => (
                  <option key={profile} value={profile}>
                    {profile}
                  </option>
                ))}
              </select>
            </div>
            {selectedUPN && (
              <>
                <div className="flex calculatorform-input">
                  <h4 className="fs-300">Height, h</h4>
                  <input
                    type="text"
                    value={`${selectedUPN.h} mm`}
                    readOnly
                    style={{
                      backgroundColor: "#f5f5f5",
                      cursor: "not-allowed",
                    }}
                  />
                </div>
                <div className="flex calculatorform-input">
                  <h4 className="fs-300">Width, b</h4>
                  <input
                    type="text"
                    value={`${selectedUPN.b} mm`}
                    readOnly
                    style={{
                      backgroundColor: "#f5f5f5",
                      cursor: "not-allowed",
                    }}
                  />
                </div>
                <div className="flex calculatorform-input">
                  <h4 className="fs-300">Thickness, s</h4>
                  <input
                    type="text"
                    value={`${selectedUPN.s} mm`}
                    readOnly
                    style={{
                      backgroundColor: "#f5f5f5",
                      cursor: "not-allowed",
                    }}
                  />
                </div>
                <div className="flex calculatorform-input">
                  <h4 className="fs-300">Thickness, t</h4>
                  <input
                    type="text"
                    value={`${selectedUPN.t} mm`}
                    readOnly
                    style={{
                      backgroundColor: "#f5f5f5",
                      cursor: "not-allowed",
                    }}
                  />
                </div>
              </>
            )}
            <div className="flex calculatorform-input">
              <h4 className="fs-300">Length, L</h4>
              <input
                type="number"
                placeholder="0 m"
                value={inputs.length}
                onChange={(e) => handleInputChange("length", e.target.value)}
              />
            </div>
          </>
        );

      case "channels-gost":
        const selectedGOST = inputs.profile && GOST_PROFILES[inputs.profile];
        return (
          <>
            <div className="flex calculatorform-input">
              <h4 className="fs-300">Profile</h4>
              <select
                value={inputs.profile}
                onChange={(e) => handleInputChange("profile", e.target.value)}
                style={{
                  border: "1px solid #3e784d",
                  padding: "4px",
                  width: "120px",
                  backgroundColor: "transparent",
                }}
              >
                <option value="">Select</option>
                {Object.keys(GOST_PROFILES).map((profile) => (
                  <option key={profile} value={profile}>
                    {profile}
                  </option>
                ))}
              </select>
            </div>
            {selectedGOST && (
              <>
                <div className="flex calculatorform-input">
                  <h4 className="fs-300">Height, h</h4>
                  <input
                    type="text"
                    value={`${selectedGOST.h} mm`}
                    readOnly
                    style={{
                      backgroundColor: "#f5f5f5",
                      cursor: "not-allowed",
                    }}
                  />
                </div>
                <div className="flex calculatorform-input">
                  <h4 className="fs-300">Width, b</h4>
                  <input
                    type="text"
                    value={`${selectedGOST.b} mm`}
                    readOnly
                    style={{
                      backgroundColor: "#f5f5f5",
                      cursor: "not-allowed",
                    }}
                  />
                </div>
                <div className="flex calculatorform-input">
                  <h4 className="fs-300">Thickness, s</h4>
                  <input
                    type="text"
                    value={`${selectedGOST.s} mm`}
                    readOnly
                    style={{
                      backgroundColor: "#f5f5f5",
                      cursor: "not-allowed",
                    }}
                  />
                </div>
                <div className="flex calculatorform-input">
                  <h4 className="fs-300">Thickness, t</h4>
                  <input
                    type="text"
                    value={`${selectedGOST.t} mm`}
                    readOnly
                    style={{
                      backgroundColor: "#f5f5f5",
                      cursor: "not-allowed",
                    }}
                  />
                </div>
              </>
            )}
            <div className="flex calculatorform-input">
              <h4 className="fs-300">Length, L</h4>
              <input
                type="number"
                placeholder="0 m"
                value={inputs.length}
                onChange={(e) => handleInputChange("length", e.target.value)}
              />
            </div>
          </>
        );

      default:
        return null;
    }
  };

  const getImageSrc = () => {
    const imageMap = {
      "steel-sheets": "/assets/icons/calculator/1.png",
      "seamless-circular": "/assets/icons/calculator/2.png",
      "hollow-circular": "/assets/icons/calculator/3.png",
      "hollow-square": "/assets/icons/calculator/4.png",
      "hollow-rectangular": "/assets/icons/calculator/5.png",
      "round-bars": "/assets/icons/calculator/6.png",
      "square-bars": "/assets/icons/calculator/7.png",
      "flat-bars": "/assets/icons/calculator/8.png",
      "equal-angles": "/assets/icons/calculator/9.png",
      "unequal-angles": "/assets/icons/calculator/10.png",
      "channels-gost": "/assets/icons/calculator/11.png",
      "channels-upn": "/assets/icons/calculator/12.png",
      "beams-ipn": "/assets/icons/calculator/13.png",
      "beams-heb": "/assets/icons/calculator/14.png",
      "beams-hea": "/assets/icons/calculator/15.png",
      "beams-ipe": "/assets/icons/calculator/16.png",
    };
    return imageMap[selectedShape];
  };

  const getCategoryTitle = () => {
    const titleMap = {
      "steel-sheets": "Steel sheets and plates",
      "seamless-circular": "Seamless steel pipes - circular",
      "hollow-circular": "Hollow structural sections - circular",
      "hollow-square": "Hollow structural sections - square",
      "hollow-rectangular": "Hollow structural sections - rectangular",
      "round-bars": "Round steel bars",
      "square-bars": "Square steel bars",
      "flat-bars": "Flat bars",
      "equal-angles": "Equal angles",
      "unequal-angles": "Unequal angles",
      "channels-gost": "Channels - GOST",
      "channels-upn": "Channels - UPN",
      "beams-ipn": "Beams - IPN",
      "beams-heb": "Beams - HEB (IPB)",
      "beams-hea": "Beams - HEA (IPBL)",
      "beams-ipe": "Beams - IPE",
    };
    return titleMap[selectedShape] || "";
  };

  return (
    <div className="calculatorform-wrapper">
      <div className="calculatorform container">
        <h2
          ref={titleRef}
          className={`uppercase fs-800 clr-white ${
            titleVisible ? "animate-in" : ""
          }`}
        >
          Steel Calculator
        </h2>
        <p
          ref={descRef}
          className={`fs-400 clr-white calculatorform-description ${
            descVisible ? "animate-in" : ""
          }`}
        >
          Calculate approximate material requirements by entering your steel
          parameters below
        </p>
        <div
          ref={contentRef}
          className={`flex calculatorform-content ${
            contentVisible ? "animate-in" : ""
          }`}
        >
          <div className="calculatorform-buttons flex">
            <button
              className={`fs-500 clr-white ${
                selectedShape === "steel-sheets" ? "active" : ""
              }`}
              onClick={() => handleShapeChange("steel-sheets")}
            >
              Steel sheets and plates
            </button>
            <button
              className={`fs-500 clr-white ${
                selectedShape === "seamless-circular" ? "active" : ""
              }`}
              onClick={() => handleShapeChange("seamless-circular")}
            >
              Seamless steel pipes - circular
            </button>
            <button
              className={`fs-500 clr-white ${
                selectedShape === "hollow-circular" ? "active" : ""
              }`}
              onClick={() => handleShapeChange("hollow-circular")}
            >
              Hollow structural sections - circular
            </button>
            <button
              className={`fs-500 clr-white ${
                selectedShape === "hollow-square" ? "active" : ""
              }`}
              onClick={() => handleShapeChange("hollow-square")}
            >
              Hollow structural sections - square
            </button>
            <button
              className={`fs-500 clr-white ${
                selectedShape === "hollow-rectangular" ? "active" : ""
              }`}
              onClick={() => handleShapeChange("hollow-rectangular")}
            >
              Hollow structural sections - rectangular
            </button>
            <button
              className={`fs-500 clr-white ${
                selectedShape === "round-bars" ? "active" : ""
              }`}
              onClick={() => handleShapeChange("round-bars")}
            >
              Round steel bars
            </button>
            <button
              className={`fs-500 clr-white ${
                selectedShape === "square-bars" ? "active" : ""
              }`}
              onClick={() => handleShapeChange("square-bars")}
            >
              Square steel bars
            </button>
            <button
              className={`fs-500 clr-white ${
                selectedShape === "flat-bars" ? "active" : ""
              }`}
              onClick={() => handleShapeChange("flat-bars")}
            >
              Flat bars
            </button>
            <button
              className={`fs-500 clr-white ${
                selectedShape === "equal-angles" ? "active" : ""
              }`}
              onClick={() => handleShapeChange("equal-angles")}
            >
              Equal angles
            </button>
            <button
              className={`fs-500 clr-white ${
                selectedShape === "unequal-angles" ? "active" : ""
              }`}
              onClick={() => handleShapeChange("unequal-angles")}
            >
              Unequal angles
            </button>
            <button
              className={`fs-500 clr-white ${
                selectedShape === "channels-gost" ? "active" : ""
              }`}
              onClick={() => handleShapeChange("channels-gost")}
            >
              Channels - GOST
            </button>
            <button
              className={`fs-500 clr-white ${
                selectedShape === "channels-upn" ? "active" : ""
              }`}
              onClick={() => handleShapeChange("channels-upn")}
            >
              Channels - UPN
            </button>
            <button
              className={`fs-500 clr-white ${
                selectedShape === "beams-ipn" ? "active" : ""
              }`}
              onClick={() => handleShapeChange("beams-ipn")}
            >
              Beams - IPN
            </button>
            <button
              className={`fs-500 clr-white ${
                selectedShape === "beams-ipe" ? "active" : ""
              }`}
              onClick={() => handleShapeChange("beams-ipe")}
            >
              Beams - IPE
            </button>
            <button
              className={`fs-500 clr-white ${
                selectedShape === "beams-hea" ? "active" : ""
              }`}
              onClick={() => handleShapeChange("beams-hea")}
            >
              Beams - HEA (IPBL)
            </button>
            <button
              className={`fs-500 clr-white ${
                selectedShape === "beams-heb" ? "active" : ""
              }`}
              onClick={() => handleShapeChange("beams-heb")}
            >
              Beams - HEB (IPB)
            </button>
          </div>
          <div className="calculatorform-white flex">
            <div>
              <p className="clr-green fs-300 calculatorform-white-description">
                Input your values in the fields below
              </p>
              <div className="calculatorform-fields">{getInputFields()}</div>
              <h3 className="clr-green fs-300">Results</h3>
              <div className="calculatorform-results">
                {selectedShape === "steel-sheets" ? (
                  <>
                    <div className="flex calculatorform-white-results">
                      <h4 className="fs-300">Single piece weight</h4>
                      <p className="fs-300">{results.singlePieceWeight} kg</p>
                    </div>
                    <div className="flex calculatorform-white-results">
                      <h4 className="fs-300">Total weight</h4>
                      <p className="fs-300">{results.totalWeight} kg</p>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex calculatorform-white-results">
                      <h4 className="fs-300">Weight per meter</h4>
                      <p className="fs-300">{results.weightPerMeter} kg/m</p>
                    </div>
                    <div className="flex calculatorform-white-results">
                      <h4 className="fs-300">Total weight</h4>
                      <p className="fs-300">{results.totalWeight} kg</p>
                    </div>
                  </>
                )}
              </div>
            </div>
            <div className="calculatorform-image-container">
              <h4 className="calculatorform-category-title">{getCategoryTitle()}</h4>
            <img src={getImageSrc()} alt={`${selectedShape} Icon`} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CalculatorForm;
